<template>
  <v-container fluid class="pa-4">
    <v-card v-click-outside="deactivateSelectionMode">
      <v-card-title class="d-flex justify-space-between align-center">
        <span class="title">{{ $t('title.asset') }}</span>
        <div class="d-flex">

          <v-btn size="small" variant="outlined" color="primary" class="mr-4 action-btn" prepend-icon="mdi-circle"
            :disabled="isActiveStatus">
            {{ $t('general.aktivna/neaktivna') }}
          </v-btn>

          <v-switch v-model="isActiveStatus" hide-details density="compact" class="mr-4" style="margin-top:-7px" />

          <v-btn size="small" variant="outlined" color="danger" class="action-btn" prepend-icon="mdi-circle"
            :disabled="!isActiveStatus">
            {{ $t('general.rashodovana') }}
          </v-btn>
          <div style="width: 100px;" />
          <v-tooltip location="bottom" content-class="custom-tooltip">
            <template #activator="{ props }">
              <v-btn v-bind="props" size="small" variant="outlined" color="primary" class="mr-2 action-btn"
                icon="mdi-eye" @click="printAssets" />
            </template>
            {{ $t('general.viewprint') }}
          </v-tooltip>

          <v-tooltip location="bottom" content-class="custom-tooltip">
            <template #activator="{ props }">
              <v-btn v-bind="props" size="small" :variant="selectionMode ? 'flat' : 'outlined'"
                :color="selectionMode ? 'error' : 'secondary'" class="mr-2 action-btn" icon="mdi-barcode-scan"
                @click="toggleSelectionMode" />
            </template>
            {{ selectionMode ? $t('general.odustaniBarKod') : $t('general.odaberiBarKodZaStampu') }}
          </v-tooltip>

          <!-- Štampaj dugme — pojavljuje se samo kad je selectionMode aktivan -->
          <v-tooltip v-if="selectionMode" location="bottom" content-class="custom-tooltip">
            <template #activator="{ props }">
              <v-btn v-bind="props" size="small" variant="outlined" color="secondary" class="mr-2 action-btn"
                icon="mdi-barcode" :disabled="selectedAssets.length === 0" @click="printSelectedBarcodes" />
            </template>
            {{ $t('general.printBarCode') }} ({{ selectedAssets.length }})
          </v-tooltip>

          <v-tooltip location="bottom" content-class="custom-tooltip">
            <template #activator="{ props }">
              <v-btn v-bind="props" size="small" variant="outlined" color="success" class="mr-2 action-btn"
                @click="exportToExcel" icon="mdi-file-excel" />
            </template>
            {{ $t('general.exportExcel') }}
          </v-tooltip>

          <v-tooltip location="bottom" content-class="custom-tooltip">
            <template #activator="{ props }">
              <v-badge :content="popisneAkcijeCount" :model-value="popisneAkcijeCount > 0" color="error" floating
                location="top end" style="--v-badge-font-size: 9px; --v-badge-size: 10px;" offset-x="4" offset-y="9">
                <v-btn v-bind="props" size="small" variant="outlined" color="warning" class="action-btn"
                  :class="{ 'blink-btn': popisneAkcijeCount > 0 }" icon="mdi-clipboard-check-outline"
                  @click="openPopisneAkcije" />
              </v-badge>
            </template>
            {{ $t('general.popisneAkcije') }}
          </v-tooltip>

          <v-tooltip location="bottom" content-class="custom-tooltip">
            <template #activator="{ props }">
              <v-btn v-bind="props" size="small" variant="outlined" color="primary" class="ml-4 action-btn"
                icon="mdi-plus" @click="addAsset" />
            </template>
            {{ $t('general.addAsset') }}
          </v-tooltip>

        </div>
      </v-card-title>

      <v-card-text>
        <v-text-field v-model="search" prepend-inner-icon="mdi-magnify" class="mb-2" density="compact"
          autocomplete="off" />

        <!-- PRETRAGA PO PREDEFINISANIM VRIJEDNOSTIMA-->
        <v-row dense class="mb-4">

          <!-- Datum od -->
          <v-col cols="12" md="2">
            <v-text-field v-model="dateFrom" :label="$t('general.datumOd')" type="date" density="comfortable"
              hide-details="auto" clearable clear-icon="mdi-close" class="my-text" />
          </v-col>

          <!-- Datum do -->
          <v-col cols="12" md="2">
            <v-text-field v-model="dateTo" :label="$t('general.datumDo')" type="date" density="comfortable"
              hide-details="auto" clearable clear-icon="mdi-close" class="my-text" />
          </v-col>

          <!-- Kategorija -->
          <v-col cols="12" md="3">
            <v-select v-model="selectedCategory" :items="assetCategory" item-title="Name" item-value="Id"
              :label="$t('general.kategorijaSredstva')" clearable density="comfortable" hide-details="auto"
              class="my-select" />
          </v-col>

          <!-- Korisnik -->
          <v-col cols="12" md="3">
            <v-select v-model="selectedUser" :items="users" item-title="Name" item-value="UserId"
              :label="$t('general.zaduzenaOsoba')" clearable density="comfortable" hide-details="auto"
              class="my-select" />
          </v-col>

          <!-- Lokacija -->
          <v-col cols="12" md="2">
            <v-select v-model="selectedLocation" :items="locations" item-title="Name" item-value="Id"
              :label="$t('general.lokacija')" clearable density="comfortable" hide-details="auto" class="my-select" />
          </v-col>


        </v-row>
        <!-- KRAJ PRETRAGE PO PREDEFINISANIM VRIJEDNOSTIMA-->

        <v-data-table :headers="headers" :items="filteredAssets" :loading="loading"
          :loading-text="$t('general.loading')" class="min-width-table custom-header" :items-per-page="15">

          <!-- Bold naslovi i sort ikonice -->
          <template v-slot:headers="{ columns, toggleSort, isSorted, getSortIcon }">
            <tr>
              <th v-if="selectionMode" style="width:60px; text-align:center;">
                <v-checkbox v-model="selectAll" hide-details density="compact" @update:model-value="toggleSelectAll" />
              </th>
              <th v-for="column in columns.filter(c => c.value !== 'select')" :key="column.value"
                @click="() => column.sortable && toggleSort(column)" :style="{
                  cursor: column.sortable ? 'pointer' : 'default',
                  width: column.width,
                  maxWidth: column.width,
                  textAlign:
                    column.value === 'actions'
                      ? 'right'
                      : column.value === 'StatusName'
                        ? 'center'
                        : 'left'
                }" class="sortable-header">

                <div class="d-flex align-center w-100" :class="column.value === 'actions'
                  ? 'justify-end'
                  : column.value === 'StatusName'
                    ? 'justify-center'
                    : 'justify-space-between'
                  ">
                  <strong>{{ column.text }}</strong>

                  <v-icon v-if="column.sortable" size="12" class="sort-icon ml-2" :class="{ active: isSorted(column) }">
                    {{ isSorted(column) ? getSortIcon(column) : 'mdi-arrow-up-down' }}
                  </v-icon>
                </div>
              </th>
            </tr>
          </template>

          <!-- Redovi -->
          <template v-slot:item="{ item }">
            <tr :class="rowClass(item)">
              <td v-if="selectionMode" style="text-align:center;">
                <v-checkbox v-model="selectedAssets" :value="item.Id" hide-details density="compact" />
              </td>
              <td> {{ item.InvNumber }}</td>
              <td>{{ item.Name }}</td>
              <td :style="!item.UserId ? { color: 'red', fontWeight: '600' } : {}">
                {{ item.UName || $t('general.nerasporedjeno') }}
              </td>
              <td>{{ item.LocationName }}</td>
              <td style="text-align: right;">{{ formatMoney(item.Price) }}</td>
              <td style="text-align: center;">{{ formatDate(item.Date) }}</td>
              <td style="text-align: right;">{{ formatMoney(item.TKV) }}</td>
              <td style="text-align: center;">
                <div class="d-flex justify-center align-center">
                  <v-menu location="bottom" offset="4">

                    <template #activator="{ props }">
                      <v-btn v-bind="props" variant="text" class="status-btn">
                        <img v-if="parseInt(item.StatusId) === 1" :src="statusImages.aktivan" height="22" />
                        <img v-if="parseInt(item.StatusId) === 2" :src="statusImages.neaktivan" height="22" />
                        <img v-if="parseInt(item.StatusId) === 3" :src="statusImages.rashodovan" height="22" />
                        <img v-if="parseInt(item.StatusId) === 4" :src="statusImages.predlogRashoda" height="22" />
                      </v-btn>
                    </template>

                    <v-list density="compact">
                      <v-list-item v-for="s in statusOptions.filter(s => isStatusAllowed(item.StatusId, s.id))"
                        :key="s.id" :disabled="s.id === item.StatusId"
                        @click="s.id !== item.StatusId && askConfirmStatusChange(item, s.id)">
                        <v-list-item-title class="d-flex align-center justify-space-between">
                          {{ s.name }}
                          <v-icon v-if="s.id === item.StatusId" size="14">mdi-check</v-icon>
                        </v-list-item-title>
                      </v-list-item>
                    </v-list>

                  </v-menu>
                </div>
              </td>

              <!-- IKONICE-->
              <td class="actions-cell">
                <div class="actions-main">
                  <!-- ULAGANJE -->
                  <v-tooltip location="bottom" content-class="custom-tooltip">
                    <template #activator="{ props }">
                      <router-link
                        :to="{ name: 'AssetInvestments', params: { assetId: item.Id }, query: { amValue: item.CategoryValue } }"
                        class="inv-link">
                        <v-btn v-bind="props" size="small" variant="outlined" color="info" class="action-btn mr-2"
                          @click="viewAsset(item)">
                          <v-icon size="16">mdi-cash-plus</v-icon>
                        </v-btn>
                      </router-link>
                    </template>
                    {{ $t('general.ulaganje') }}
                  </v-tooltip>

                  <!-- PREGLED -->
                  <v-tooltip location="bottom" content-class="custom-tooltip">
                    <template #activator="{ props }">
                      <v-btn v-bind="props" size="small" variant="outlined" color="info" class="action-btn mr-2"
                        @click="viewAsset(item)">
                        <v-icon size="16">mdi-eye</v-icon>
                      </v-btn>
                    </template>
                    {{ $t('general.pregled') }}
                  </v-tooltip>

                  <!-- UREDI -->
                  <v-tooltip location="bottom" content-class="custom-tooltip">
                    <template #activator="{ props }">
                      <v-btn v-bind="props" size="small" variant="outlined" color="primary" class="action-btn mr-2"
                        @click="editAsset(item)">
                        <v-icon size="16">mdi-pencil</v-icon>
                      </v-btn>
                    </template>
                    {{ $t('general.uredi') }}
                  </v-tooltip>

                  <!-- MORE -->
                  <v-menu location="bottom end">

                    <template #activator="{ props: menuProps }">

                      <v-tooltip location="bottom" content-class="custom-tooltip">

                        <template #activator="{ props: tooltipProps }">
                          <v-btn v-bind="{ ...menuProps, ...tooltipProps }" size="small" variant="outlined"
                            class="action-btn">
                            <v-icon>mdi-dots-horizontal</v-icon>
                          </v-btn>
                        </template>

                        {{ $t('general.more') }}
                      </v-tooltip>

                    </template>

                    <v-list density="compact" style="font-size: 14px;">
                      <v-list-item @click="openDeleteDialog(item)">
                        <v-icon start color="red">mdi-delete</v-icon>
                        {{ $t('general.delete') }}
                      </v-list-item>

                      <v-list-item @click="printBarcode(item)">
                        <v-icon start>mdi-barcode</v-icon>
                        {{ $t('general.barCode') }}
                      </v-list-item>

                      <v-list-item @click="printZaduzenje(item)">
                        <v-icon start>mdi-clipboard-account</v-icon>
                        {{ $t('general.zaduzenje') }}
                      </v-list-item>

                      <v-list-item @click="printRazduzenje(item)">
                        <v-icon start>mdi-account-remove</v-icon>
                        {{ $t('general.razduzenje') }}
                      </v-list-item>

                      <v-list-item @click="openHistory(item.Id)">
                        <v-icon start>mdi-history</v-icon>
                        {{ $t('general.Istorija') }}
                      </v-list-item>
                    </v-list>

                  </v-menu>
                </div>
              </td>
              <!-- KRAJ IKONICA-->
            </tr>
          </template>

        </v-data-table>

      </v-card-text>
    </v-card>

    <!-- Dijalog za dodavanje/uređivanje sredstva -->
    <BaseModal v-model="dialog" size="xl" :title="viewMode
      ? this.$t('modalni.asset.pregled')
      : editMode
        ? this.$t('modalni.asset.uredi')
        : this.$t('modalni.asset.dodaj')" :is-read-only="viewMode">
      <v-form ref="assetForm" v-model="isFormValid">

        <!-- ================ ROW 1 ================= -->
        <v-row dense>
          <v-col cols="12" md="3">
            <v-select v-model="form.category" :items="assetCategory" item-title="Name" item-value="Id"
              :label="$t('table.assetFull.Kategorija')" :rules="[rules.required]" required :readonly="viewMode"
              class="my-selectModal" />
          </v-col>

          <v-col cols="12" md="3">
            <v-text-field v-model="form.InvNumber" :label="$t('table.assetFull.InvBr')" :rules="[rules.required]"
              required :readonly="viewMode" class="my-text" autocomplete="off" />
          </v-col>

          <v-col cols="12" md="3">
            <v-text-field v-model="form.SerialNumber" :label="$t('table.assetFull.SerijskiBroj')" :readonly="viewMode"
              class="my-text" autocomplete="off" />
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field v-model="form.ReverseNumber" :label="$t('table.assetFull.BrojReversa')" :readonly="viewMode"
              class="my-text" autocomplete="off" />
          </v-col>
        </v-row>

        <!-- ================= ROW 2 ================= -->
        <v-row dense>
          <v-col cols="12" md="6">
            <v-text-field v-model="form.AssetName" :label="$t('table.assetFull.Naziv')" :rules="[rules.required]"
              required :readonly="viewMode" class="my-text" autocomplete="off" />
          </v-col>

          <v-col cols="12" md="2">
            <v-text-field v-model="form.Quantity" :label="$t('table.assetFull.Kolicina')" type="number" min="1"
              :rules="[rules.required]" required :readonly="viewMode" class="my-text" autocomplete="off" />
          </v-col>

          <v-col cols="12" md="4">
            <v-text-field v-model="form.PurchasePrice" :label="$t('table.assetFull.NabavnaVr')" v-money
              :rules="[rules.required]" required :readonly="viewMode" class="my-text" autocomplete="off" />

          </v-col>
        </v-row>


        <!-- ================= ROW 3  ================= -->
        <v-row dense>
          <v-col cols="12" md="3">
            <v-select v-model="form.assigned_to" :items="users" item-title="Name" item-value="UserId"
              :return-object="false" :label="$t('table.assetFull.ZaduzenaOsoba')" :rules="[rules.required]" required
              :readonly="viewMode" class="my-selectModal" />
          </v-col>
          <v-col cols="12" md="3">
            <v-select v-model="form.location" :items="locations" item-title="Name" item-value="Id"
              :label="$t('table.assetFull.Lokacija')" :rules="[rules.required]" required
              @update:model-value="onLocationChange" :readonly="viewMode" class="my-selectModal" />
          </v-col>
          <v-col cols="12" md="3">
            <v-select v-model="form.room" :items="rooms" item-title="Name" item-value="Id"
              :label="$t('table.assetFull.Soba')" :disabled="!form.location" :readonly="viewMode" clearable
              :rules="[rules.required]" required class="my-selectModal" />
          </v-col>

          <template v-if="editMode || viewMode">
            <v-col cols="12" md="3">
              <v-select v-model="form.StatusId"
                :items="statusOptions.filter(s => isStatusAllowed(form.StatusId, s.id) || s.id === form.StatusId)"
                item-title="name" item-value="id" :label="$t('table.assetFull.Status')" :rules="[rules.required]"
                required :readonly="viewMode" :disabled="form.StatusId === 3" class="my-selectModal" />
            </v-col>
          </template>
        </v-row>

        <!-- ================= ROW 4  ================= -->
        <v-row dense>

          <v-col cols="12" md="2">
            <v-text-field v-model="form.Date" :label="$t('table.assetFull.DatumNabavke')" type="date"
              :rules="[rules.required]" required :readonly="viewMode" clearable clear-icon="mdi-close" class="my-text"
              autocomplete="off" />
          </v-col>

          <v-col cols="12" md="6">
            <v-text-field v-model="form.Supplier" :label="$t('table.assetFull.Dobavljač')" :readonly="viewMode"
              class="my-text" autocomplete="off" />
          </v-col>

          <v-col cols="12" md="4">
            <v-text-field v-model="form.PrNumber" :label="$t('table.assetFull.BrojNabavke')" :readonly="viewMode"
              class="my-text" autocomplete="off" />
          </v-col>
        </v-row>

        <!-- ================= ROW 5 ================= -->
        <v-row dense>
          <v-col cols="12" md="6">
            <v-textarea v-model="form.AssetDescription" :label="$t('table.assetFull.Opis')" rows="3" auto-grow
              :readonly="viewMode" class="my-text" autocomplete="off" />
          </v-col>

          <v-col cols="12" md="6">
            <v-textarea v-model="form.Note" :label="$t('table.assetFull.Napomena')" rows="3" auto-grow
              :readonly="viewMode" class="my-text" autocomplete="off" />
          </v-col>
        </v-row>
      </v-form>

      <template #footer>
        <div class="d-flex justify-end ga-2">

          <!-- OTKAŽI -->
          <v-btn variant="outlined" @click="closeDialog">
            <span style="font-size:14px; font-weight: 600;">{{ $t('general.cancel') }}</span>
          </v-btn>

          <!-- SAČUVAJ -->
          <v-btn v-if="!viewMode" color="primary" variant="flat" @click.prevent="saveAsset" :loading="saving"
            :disabled="saving">
            <span style="font-size:14px; font-weight: 600;">{{ $t('general.save') }}</span>
          </v-btn>
        </div>
      </template>

    </BaseModal>

    <!-- ISTORIJA -->
    <BaseModal v-model="historyDialog" :title="this.$t('modalni.asset.IstorijaStalnogSredstva')" :is-read-only="true"
      size="lg">
      <div class="mb-4" style="font-size: 14px;">
        <strong>{{ this.$t('modalni.asset.DatumNabavke') }} </strong>
        <span v-if="activationDateValue">
          {{ formatDate(activationDateValue) }}
        </span>

        <span v-else>—</span>
      </div>

      <v-divider class="mb-1" />

      <v-list density="comfortable">
        <v-list-item v-for="h in assetHistory" :key="h.id">
          <v-list-item-title style="font-size: 14px;">
            <strong>{{ formatDate(h.date) }}</strong> – {{ h.text }}
          </v-list-item-title>
        </v-list-item>

        <v-list-item v-if="assetHistory.length === 0">
          <v-list-item-title
            style="font-size: 14px; text-transform: uppercase; text-align: center; font-weight: bold;">{{
              $t('User_message.m122') }}</v-list-item-title>
        </v-list-item>
      </v-list>

      <template #footer>
        <div class="d-flex justify-end ga-2">
          <v-btn color="primary" variant="flat" @click="historyDialog = false">
            <span style="font-size:14px; font-weight: 600;">{{ $t('general.close') }}</span>
          </v-btn>
        </div>
      </template>
    </BaseModal>

    <!-- PROMJENA STATUSA -->
    <BaseModal v-model="confirmStatusDialog" :title="this.$t('modalni.asset.promjenaStatusaModalni')">
      <p class="confirm-text" style="font-size: 14px;">
        {{ this.$t('modalni.asset.promjenaStatusaPoruka1') }}<strong>{{ pendingStatusChange?.asset?.Name
        }}</strong>{{
            this.$t('modalni.asset.promjenaStatusaPoruka2') }}
        <strong>
          {{statusOptions.find(s => s.id === pendingStatusChange?.newStatusId)?.name}}
        </strong>?
      </p>

      <!-- NAPOMENA -->
      <v-textarea v-model="statusChangeNote" :label="$t('table.assetFull.Napomena')" rows="3" auto-grow
        class="my-text mt-3" autocomplete="off" />

      <template #footer>
        <div class="d-flex justify-end ga-2">
          <v-btn variant="outlined" @click="cancelStatusChange">
            <span style="font-size:14px; font-weight: 600;">{{ this.$t('general.cancel') }}</span>
          </v-btn>

          <v-btn color="primary" @click="confirmStatusChange">
            <span style="font-size:14px; font-weight: 600;">{{ this.$t('general.potvrdi') }}</span>
          </v-btn>
        </div>
      </template>
    </BaseModal>


    <!-- Dijalog za potvrdu brisanja sredstva -->
    <BaseModal v-model="deleteDialog" :title="this.$t('modalni.asset.brisi')">
      <p>{{ this.$t('modalni.asset.brisanjePoruka') }} <strong>{{ deleteAssetName }}</strong>?</p>

      <template #footer>
        <div class="d-flex justify-end ga-2">
          <!-- OTKAŽI -->
          <v-btn variant="outlined" @click="deleteDialog = false">
            <span style="font-size:14px; font-weight: 600;">{{ this.$t('general.cancel') }}</span>
          </v-btn>

          <!-- OBRIŠI -->
          <v-btn color="danger" variant="flat" @click="confirmDelete">
            <span style="font-size:14px; font-weight: 600; color: #FFF">{{ this.$t('general.deleteButton') }}</span>
          </v-btn>
        </div>
      </template>
    </BaseModal>

    <!--Save/Print dialog-->
    <BaseModal v-model="reportPreview" size="xl" :title="$t('print.listaStalnihSredstava')" height="90vh"
      modalClass="report-modal" :hideFooter="true">

      <template #header-actions>

        <v-tooltip location="bottom" content-class="custom-tooltip" :text="$t('general.download')">
          <template #activator="{ props }">
            <v-btn v-bind="props" icon variant="plain" @click="downloadPDF">
              <v-icon size="18"
                style="padding-top: 2px !important; padding-left: 10px !important;">mdi-download</v-icon>
            </v-btn>
          </template>
        </v-tooltip>

        <v-tooltip location="bottom" content-class="custom-tooltip" :text="$t('general.print')">
          <template #activator="{ props }">
            <v-btn v-bind="props" icon variant="plain" @click="printReport">
              <v-icon size="18">mdi-printer</v-icon>
            </v-btn>
          </template>
        </v-tooltip>

      </template>

      <iframe ref="reportFrame" :style="{
        width: '100%',
        height: '100%',
        border: 'none',
        transform: `scale(${zoom})`,
        transformOrigin: 'top left'
      }" />

    </BaseModal>

    <!-- POPISNE AKTIVNOSTI -->
    <BaseModal v-model="popisneAkcijeDialog" :title="$t('general.popisneAktivnostiUPPER')" size="xl">
      <v-data-table :headers="headersPopisneAkcije" :items="popisneAkcijeAssets" :loading="popisneAkcijeLoading"
        class="custom-header" :items-per-page="15" density="compact">
        <template v-slot:headers="{ columns, toggleSort, isSorted, getSortIcon }">
          <tr>
            <th v-for="column in columns" :key="column.key" @click="() => column.sortable && toggleSort(column)"
              :style="{ cursor: column.sortable ? 'pointer' : 'default', width: column.width }" class="sortable-header">
              <div class="d-flex align-center justify-space-between">
                <strong>{{ column.title }}</strong>
                <v-icon v-if="column.sortable" size="12" class="sort-icon ml-2" :class="{ active: isSorted(column) }">
                  {{ isSorted(column) ? getSortIcon(column) : 'mdi-arrow-up-down' }}
                </v-icon>
              </div>
            </th>
          </tr>
        </template>

        <template v-slot:[`item.InvNumber`]="{ item }">{{ item.InvNumber }}</template>
        <template v-slot:[`item.Name`]="{ item }">{{ item.Name }}</template>
        <template v-slot:[`item.UserName`]="{ item }">{{ item.Name || '-' }}</template>
        <template v-slot:[`item.LocationName`]="{ item }">{{ item.LocationName || '-' }}</template>
        <template v-slot:[`item.Note`]="{ item }">{{ item.Note || '-' }}</template>

        <template v-slot:[`item.Actions`]="{ item }">
          <div class="d-flex justify-center ga-2">

            <v-tooltip location="bottom" content-class="custom-tooltip">
              <template #activator="{ props }">
                <v-btn v-bind="props" size="small" variant="outlined" color="success" class="action-btn"
                  @click="openPopisneAkcijeEdit(item)">
                  <v-icon size="16">mdi-pencil</v-icon>
                </v-btn>
              </template>
              {{ $t('general.prihvati') }}
            </v-tooltip>

            <v-tooltip location="bottom" content-class="custom-tooltip">
              <template #activator="{ props }">
                <v-btn v-bind="props" size="small" variant="outlined" color="secondary" class="action-btn"
                  @click="odbijAkciju(item)">
                  <v-icon size="16">mdi-close</v-icon>
                </v-btn>
              </template>
              {{ $t('general.procitano') }}
            </v-tooltip>

          </div>
        </template>
      </v-data-table>

      <template #footer>
        <div class="d-flex justify-end ga-2">
          <v-btn variant="outlined" @click="popisneAkcijeDialog = false">
            <span style="font-size:14px; font-weight:600;">{{ $t('general.close') }}</span>
          </v-btn>
        </div>
      </template>
    </BaseModal>
  </v-container>
</template>

<script>
import JsBarcode from "jsbarcode"
import BaseModal from "@/components/dialogs/BaseModal.vue";
import { format } from "date-fns";
import ExcelJS from "exceljs"
import { saveAs } from "file-saver";
import { printTable, fetchHeaderImage } from "@/services/printService";
import html2pdf from "html2pdf.js"
import { printBarCodeF } from "@/services/printBarCode";

export default {
  name: "AssetsPage",

  data() {
    return {
      saving: false,
      html: '',
      zoom: 1,
      reportPreview: false,
      selectedUser: null,
      selectedLocation: null,
      activationDateValue: null,
      confirmStatusDialog: false,
      popisneAkcijeCount: 0,
      popisneAkcijeEditItem: null,
      pendingStatusChange: {
        asset: null,
        newStatusId: null,
        oldStatusId: null,
      },
      viewMode: false,
      selectionMode: false,
      isFormValid: false,
      deleteDialog: false,
      deleteAssetId: null,
      deleteAssetName: '',
      search: "",
      statusChangeNote: '',
      dateFrom: null,
      dateTo: null,
      selectedCategory: null,
      assets: [],
      users: [],
      loading: false,
      dialog: false,
      editMode: false,
      selectedId: null,
      assetCategory: [],
      locations: [],
      rooms: [],
      selectedAssets: [],
      popisneAkcijeDialog: false,
      popisneAkcijeLoading: false,
      popisneAkcijeAssets: [],

      rules: {
        required: v => (v !== null && v !== undefined && v !== '') || this.$t('systemMessage.m1'),
      },

      form: {
        InvNumber: 0,          // Inv. broj
        ReverseNumber: "",      // Broj reversa
        AssetName: "",          // Sredstvo
        category: null,
        assigned_to: null,       // Zadužena osoba
        room: "",                // Soba
        location: "",            // Lokacija
        PurchasePrice: null,   // Nabavna vr.
        Date: null,    // Datum nabavke
        AssetDescription: "",  // Opis sredstva
        Note: "",              // Napomena
        Supplier: "",            // Dobavljač
        PrNumber: "",           // Broj PR
        Quantity: 1,             // Kom.
        SerialNumber: "",       // S/N
        StatusId: null,         // Status
        AmortizacijaValue: "",
        TKV: null
      },

      historyDialog: false,
      assetHistory: [],
      isActiveStatus: false
    };
  },

  computed: {
    selectAll: {
      get() {
        return this.filteredAssets.length > 0 &&
          this.filteredAssets.every(a => this.selectedAssets.includes(a.Id))
      },
      set() {
      }
    },

    imageLang() {
      const lang = localStorage.getItem('lang')
      if (lang === 'sr' || lang === 'cir-ekavica') return 'Cir'
      if (lang === 'en') return 'Eng'
      return 'Lat'
    },
    statusImages() {
      const lang = this.imageLang
      return {
        aktivan: `/img/aktivan${lang}.png`,
        neaktivan: `/img/neaktivan${lang}.png`,
        rashodovan: `/img/rashodovan${lang}.png`,
        predlogRashoda: `/img/predlogRashoda${lang}.png`,
      }
    },
    headers() {
      return [
        { text: '', value: 'select', sortable: false, width: "60px" },
        { text: this.$t('table.asset.InvBr'), value: "InvNumber", sortable: true, width: "7.5%" },
        { text: this.$t('table.asset.naziv'), value: "Name", sortable: true, width: 'calc(37% - 280px)' }, // ← AssetName ili Name?
        { text: this.$t('table.asset.zaduzenaOsoba'), value: "UName", sortable: true, width: "12%" }, // ← UserName ili UName?
        { text: this.$t('table.asset.lokacija'), value: "LocationName", sortable: true, width: "8%" },
        { text: this.$t('table.asset.NabavnaVr'), value: "Price", sortable: true, width: "8%" }, // ← PurchasePrice ili Price?
        { text: this.$t('table.asset.DatumNabavke'), value: "Date", sortable: true, width: "9%" },
        { text: this.$t('table.asset.TKV'), value: "TKV", sortable: true, width: "8%" },
        { text: this.$t('table.asset.status'), value: "StatusName", sortable: false, width: "130px" },
        { text: this.$t('general.optionsUPPER'), value: "actions", sortable: false, width: "200px" },
      ]
    },
    statusOptions() {
      return [
        { id: 1, name: this.$t('general.aktivan') },
        { id: 2, name: this.$t('general.neaktivan') },
        { id: 3, name: this.$t('general.rashodovan') },
        { id: 4, name: this.$t('general.predlogZaRashod') },
      ]
    },
    headersPopisneAkcije() {
      return [
        { title: this.$t('table.asset.InvBr'), key: 'InvNumber', sortable: true, width: '10%' },
        { title: this.$t('table.asset.naziv'), key: 'Name', sortable: true, width: '22%' },
        { title: this.$t('table.asset.zaduzenaOsoba'), key: 'UserName', sortable: true, width: '15%' },
        { title: this.$t('table.assetFull.Lokacija'), key: 'LocationName', sortable: true, width: '10%' },
        { title: this.$t('table.assetFull.Napomena'), key: 'Note', sortable: false, width: '35%' },
        { title: this.$t('general.options'), key: 'Actions', sortable: false, width: '8%' },
      ]
    },
    filteredAssets() {
      const term = (this.search || "").toLowerCase();
      const nerasporedjeno = this.$t('general.nerasporedjeno').toLowerCase()

      const filtered = this.assets.filter(asset => {
        const displayName = asset.UName
          ? String(asset.UName).toLowerCase()
          : nerasporedjeno

        const matchesSearch =
          String(asset.InvNumber ?? "").toLowerCase().includes(term) ||
          String(asset.Name ?? "").toLowerCase().includes(term) ||
          displayName.includes(term) ||
          String(asset.LocationName ?? "").toLowerCase().includes(term) ||
          String(asset.StatusName ?? "").toLowerCase().includes(term) ||
          String(asset.Price ?? "").toLowerCase().includes(term)

        let matchesDate = true;
        if (this.dateFrom)
          matchesDate = matchesDate && asset.Date && new Date(asset.Date) >= new Date(this.dateFrom)
        if (this.dateTo)
          matchesDate = matchesDate && asset.Date && new Date(asset.Date) <= new Date(this.dateTo)

        const matchesCategory =
          !this.selectedCategory ||
          Number(asset.CategoryId) === Number(this.selectedCategory)

        const matchesUser =
          this.selectedUser === null ||
          (this.selectedUser === 0
            ? !asset.UserId
            : Number(asset.UserId) === Number(this.selectedUser))

        const matchesLocation =
          !this.selectedLocation ||
          Number(asset.LocationId) === Number(this.selectedLocation)

        return matchesSearch && matchesDate && matchesCategory && matchesUser && matchesLocation
      })
      return filtered
    },
  },

  watch: {
    isActiveStatus() {
      this.loadAssets()
    },
    'form.location'(newVal, oldVal) {
      if (this.editMode && newVal !== oldVal) {
        this.form.room = null

        this.$nextTick(() => {
          this.$refs.assetForm?.validate()
        })
      }
    }
  },
  components: {
    BaseModal,
  },

  async mounted() {
    await Promise.all([
      this.fetchUsers(),
      this.fetchAssetCategories(),
      this.fetchLocations(),
      this.fetchPopisneAkcijeCount(),
    ])
    await this.loadAssets()
  },

  methods: {
    isStatusAllowed(currentStatusId, newStatusId) {
      // Iz rashodovanog se ne može ni u šta
      if (currentStatusId === 3) return false
      // Iz aktivnog ili neaktivnog se ne može direktno u rashodovan
      if ((currentStatusId === 1 || currentStatusId === 2) && newStatusId === 3) return false
      // Iz predloga za rashod može u sve
      if (currentStatusId === 4) return true
      // Ostalo je dozvoljeno
      return true
    },
    async openPopisneAkcijeEdit(item) {
      this.popisneAkcijeEditItem = item
      await this.editAsset(item)
    },
    async fetchPopisneAkcijeCount() {
      try {
        const r = await this.$axios.post('Asset/StalnaSredstva_PopisneAktivnosti', {
          Id: Number(localStorage.getItem('CompanyId')),
          LanguageId: Number(localStorage.getItem('LanguageId'))
        })
        this.popisneAkcijeCount = (r.data || []).length
      } catch (err) {
        this.popisneAkcijeCount = 0
      }
    },
    async openPopisneAkcije() {
      this.popisneAkcijeDialog = true
      this.popisneAkcijeLoading = true
      try {
        const r = await this.$axios.post('Asset/StalnaSredstva_PopisneAktivnosti', {
          Id: Number(localStorage.getItem('CompanyId')),
          LanguageId: Number(localStorage.getItem('LanguageId'))
        })
        this.popisneAkcijeAssets = r.data || []
      } catch (err) {
        console.error(err)
        this.popisneAkcijeAssets = []
      } finally {
        this.popisneAkcijeLoading = false
      }
    },

    async prihvatiAkciju(item) {
      try {
        await this.$axios.put('Popis/PopisAction_Accept', {
          AssetId: item.Id,
          CompanyId: Number(localStorage.getItem('CompanyId'))
        })
        this.popisneAkcijeAssets = this.popisneAkcijeAssets.filter(a => a.Id !== item.Id)
        this.popisneAkcijeCount = this.popisneAkcijeAssets.length
        this.$store.dispatch('NOTIF_SUCCESS', {
          poruka: this.$t('User_message.m130'),
          position: 'top-end',
          timer: 2500,
          action: 'noreload'
        })
      } catch (err) {
        console.error(err)
      }
    },

    async odbijAkciju(item) {
      try {
        await this.$axios.put('Popis/PopisAction_Accept', {
          AssetId: item.Id,
          CompanyId: Number(localStorage.getItem('CompanyId'))
        })
        this.popisneAkcijeAssets = this.popisneAkcijeAssets.filter(a => a.Id !== item.Id)
        this.popisneAkcijeCount = this.popisneAkcijeAssets.length
        this.$store.dispatch('NOTIF_SUCCESS', {
          poruka: this.$t('User_message.m131'),
          position: 'top-end',
          timer: 2500,
          action: 'noreload'
        })
      } catch (err) {
        console.error(err)
      }
    },
    deactivateSelectionMode() {
      if (this.selectionMode) {
        this.selectionMode = false
        this.selectedAssets = []
      }
    },
    toggleSelectionMode() {
      this.selectionMode = !this.selectionMode
      if (!this.selectionMode) {
        this.selectedAssets = []
      }
    },
    toggleSelectAll(val) {
      if (val) {
        this.selectedAssets = this.filteredAssets.map(a => a.Id)
      } else {
        this.selectedAssets = []
      }
    },
    printSelectedBarcodes() {
      const selected = this.filteredAssets.filter(a =>
        this.selectedAssets.includes(a.Id)
      )
      if (!selected.length) return

      const invLabel = this.$t('table.asset.InvBr')

      // Generisanje jedne "label" div za svako sredstvo
      const labelsHtml = selected.map((asset, index) => {
        const isLast = index === selected.length - 1
        return `
      <div class="label" style="${!isLast ? 'page-break-after: always;' : ''}">
        <div class="inv">${invLabel}: ${asset.InvNumber}</div>
        <svg id="barcode-${asset.Id}"></svg>
        <div class="name">${asset.Name}</div>
      </div>
    `
      }).join('')

      const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8" />
      <title>Barkod</title>
      <style>
        @page {
          size: 4cm 2cm;
          margin: 0;
        }

        html, body {
          width: 4cm;
          margin: 0;
          padding: 0;
        }

        body {
          font-family: Arial, sans-serif;
          text-align: center;
        }

        .label {
          width: 4cm;
          height: 2cm;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
        }

        .inv {
          font-size: 8px;
          font-weight: bold;
          margin-bottom: 3px;
          line-height: 1;
          white-space: nowrap;
        }

        svg {
          width: 100%;
          max-height: 22px;
        }

        .name {
          font-size: 8px;
          margin-top: 3px;
          line-height: 1;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      </style>
    </head>
    <body>
      ${labelsHtml}
    </body>
    </html>
  `

      const iframe = document.createElement('iframe')
      iframe.style.position = 'fixed'
      iframe.style.right = '0'
      iframe.style.bottom = '0'
      iframe.style.width = '0'
      iframe.style.height = '0'
      iframe.style.border = '0'

      document.body.appendChild(iframe)

      const doc = iframe.contentWindow.document
      doc.open()
      doc.write(html)
      doc.close()

      iframe.onload = () => {
        const win = iframe.contentWindow

        // Generisanje barkoda za svako sredstvo
        selected.forEach(asset => {
          const svgEl = win.document.getElementById('barcode-' + asset.Id)
          if (svgEl) {
            JsBarcode(svgEl, String(asset.InvNumber), {
              format: 'CODE128',
              displayValue: false,
              height: 30,
              margin: 0
            })
          }
        })

        win.focus()
        win.print()
      }

      iframe.contentWindow.onafterprint = () => {
        document.body.removeChild(iframe)
        this.selectedAssets = []
        this.selectionMode = false
      }
    },

    async loadAssets() {

      if (this.isActiveStatus) {
        await this.scrappedAssets()
      } else {
        await this.fetchAssets()
      }

    },
    generateReportFileName(title) {

      const date = new Date()

      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()

      const compactDate = `${day}${month}${year}`

      const cleanTitle = title
        .replace(/[čć]/g, "c")
        .replace("ž", "z")
        .replace("š", "s")
        .replace("đ", "d")
        .split(" ")
        .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join("")

      return `${cleanTitle}_${compactDate}.pdf`
    },

    openReportPreview(html) {

      this.reportPreview = true

      this.$nextTick(() => {
        this.$nextTick(() => {

          const iframe = this.$refs.reportFrame
          if (!iframe) return

          const doc = iframe.contentWindow.document

          doc.open()
          doc.write(html)
          doc.close()

        })
      })
    },

    printReport() {
      const iframe = this.$refs.reportFrame
      iframe.contentWindow.print()
    },

    downloadPDF() {

      const container = document.createElement("div")
      container.innerHTML = this.html

      const title = container.querySelector("h2")?.innerText

      const fileName = this.generateReportFileName(title)

      html2pdf()
        .set({
          margin: 10,
          filename: fileName,
          html2canvas: { scale: 2 },
          jsPDF: {
            unit: "mm",
            format: "a4",
            orientation: "landscape"
          }
        })
        .from(container)
        .save()
    },

    parseMoney(value) {
      if (value === null || value === undefined || value === '') return null

      if (typeof value === 'number') return value

      return Number(
        value
          .replace(/\./g, '')
          .replace(',', '.')
      )
    },
    async scrappedAssets() {
      this.loading = true
      try {
        var data = {
          Id: Number(localStorage.getItem('CompanyId')),
          LanguageId: Number(localStorage.getItem('LanguageId'))
        }

        const r = await this.$axios.post('Asset/StalnaSredstva_Rashodovana/', data)

        this.assets = r.data

      } catch (err) {
        console.error(this.$t('general.noData'), err)
        this.assets = []
      } finally {
        this.loading = false
      }
    },
    formatMoney(value) {
      if (value === null || value === undefined || value === '') return '-'

      const number = Number(value)
      if (Number.isNaN(number)) return '-'

      return (
        number.toLocaleString('de-DE', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      )
    },
    async printZaduzenje(item) {
      const headerImg = await fetchHeaderImage(this.$axios)
      const columns = [
        { key: "inv", label: this.$t("table.assetFull.InvBr"), width: "12%" },
        { key: "name", label: this.$t("table.assetFull.Naziv"), width: "23%" },
        { key: "sn", label: this.$t("table.assetFull.SerijskiBroj"), width: "14%" },
        { key: "user", label: this.$t("table.assetFull.ZaduzenaOsoba"), width: "16%" },
        { key: "loc", label: this.$t("table.assetFull.Lokacija"), width: "12%" },
        { key: "room", label: this.$t("table.assetFull.Soba"), width: "10%" },
        { key: "date", label: this.$t("table.assetFull.DatumZaduzenja"), width: "13%", align: "center" },
      ]

      const data = [{
        inv: item?.InvNumber ?? "-",
        name: item?.Name ?? "-",
        sn: item?.SerialNumber ?? "-",
        user: item?.UName ?? this.$t("general.nerasporedjeno"),
        loc: item?.LocationName ?? "-",
        room: item?.RoomName ?? "-",
        date: this.formatDate(item?.Date),
      }]

      const footer = `
                        <div style="text-align:right; margin-top:20px;">
                          <strong>${this.$t("table.assetFull.ZaduzenaOsoba")}</strong><br/>
                          ${item?.UName ?? this.$t("general.nerasporedjeno")}<br/><br/>
                          ______________________________
                        </div>
                      `

      this.html = printTable({
        title: `${this.$t("print.revers")} ${item?.ReverseNumber || "-"}`,
        columns,
        data,
        footer,
        headerImg
      })
      this.openReportPreview(this.html)
    },
    async printRazduzenje(item) {
      const headerImg = await fetchHeaderImage(this.$axios)
      const columns = [
        { key: "inv", label: this.$t("table.assetFull.InvBr"), width: "12%" },
        { key: "name", label: this.$t("table.assetFull.Naziv"), width: "23%" },
        { key: "sn", label: this.$t("table.assetFull.SerijskiBroj"), width: "14%" },
        { key: "user", label: this.$t("table.assetFull.ZaduzenaOsoba"), width: "16%" },
        { key: "loc", label: this.$t("table.assetFull.Lokacija"), width: "12%" },
        { key: "room", label: this.$t("table.assetFull.Soba"), width: "10%" },
        { key: "date", label: this.$t("table.assetFull.DatumZaduzenja"), width: "13%", align: "center" },
      ]

      const data = [{
        inv: item?.InvNumber ?? "-",
        name: item?.Name ?? "-",
        sn: item?.SerialNumber ?? "-",
        user: item?.UName ?? this.$t("general.nerasporedjeno"),
        loc: item?.LocationName ?? "-",
        room: item?.RoomName ?? "-",
        date: this.formatDate(item?.Date),
      }]

      const footer = `
                        <div style="text-align:right; margin-top:20px;">
                          <strong>${this.$t("table.assetFull.RazduzenaOsoba")}</strong><br/>
                          ${item?.UName ?? this.$t("general.nerasporedjeno")}<br/><br/>
                          ______________________________
                        </div>
                      `

      this.html = printTable({
        title: `${this.$t("print.razduzenje")} ${item?.ReverseNumber ?? "-"}`,
        columns,
        data,
        footer,
        headerImg
      })
      this.openReportPreview(this.html)
    },
    async printAssets() {
      const assets = this.filteredAssets || []

      if (!assets.length) {
        this.$store.dispatch("NOTIF_WARNING", {
          poruka: this.$t("print.na"),
          position: "center",
          icon: "error",
          timer: 4500,
          width: "550",
        })
        return
      }
      const headerImg = await fetchHeaderImage(this.$axios)
      const columns = [
        { key: "inv", label: this.$t("table.asset.InvBr"), width: "8%" },
        { key: "name", label: this.$t("table.asset.naziv"), width: "25%" },
        { key: "user", label: this.$t("table.asset.zaduzenaOsoba"), width: "17%" },
        { key: "location", label: this.$t("table.assetFull.Lokacija"), width: "11%" },
        { key: "price", label: this.$t("table.asset.NabavnaVr"), width: "9%", align: "right" },
        { key: "date", label: this.$t("table.asset.DatumNabavke"), width: "12%", align: "center" },
        { key: "tkv", label: this.$t("table.asset.TKV"), width: "9%", align: "right" },
        { key: "status", label: this.$t("table.asset.status"), width: "9%", align: "center" },
      ]

      const data = assets.map((a) => ({
        inv: a.InvNumber ?? "-",
        name: a.Name ?? "-",
        user: a.UName ?? this.$t("general.nerasporedjeno"),
        location: a.LocationName ?? "-",
        price: this.formatMoney(a.Price),
        date: this.formatDate(a.Date),
        tkv: this.formatMoney(a.TKV),
        status: a.StatusName ?? "-",
      }))

      const footer = `
    ${this.$t("exportToExcel.pokrenuo")} ${localStorage.getItem("Name")} <br />
    ${this.$t("print.m1")} ${format(new Date(), "dd.MM.yyyy.")}
  `

      this.html = printTable({
        title: this.$t("print.listaStalnihSredstava"),
        columns,
        data,
        footer,
        headerImg
      })
      this.openReportPreview(this.html)
    },
    mapHistory(raw) {
      if (!Array.isArray(raw) || raw.length === 0) return []

      const history = []
      const v = (x) => (x === null || x === undefined || x === "" ? "—" : x)

      history.push({
        id: `${raw[0].Id}-initial`,
        date: raw[0].Date,
        StatusId: raw[0].StatusId,
        text: `${this.$t('modalni.asset.pocetnoStanje')}: ${v(raw[0].Name)} | ${v(raw[0].LocationName)}`
      })


      for (let i = 1; i < raw.length; i++) {
        // PROMJENA ZADUŽENJA
        if (raw[i - 1].UserId !== raw[i].UserId) {
          history.push({
            id: `${raw[i].Id}-user`,
            date: raw[i].Date,
            StatusId: raw[i].StatusId,
            text: `${this.$t('modalni.asset.promjenaZaduzenja')} ${v(raw[i - 1].Name)} → ${v(raw[i].Name)}`
          })
        }

        // PROMJENA LOKACIJE
        if (raw[i - 1].LocationId !== raw[i].LocationId) {
          history.push({
            id: `${raw[i].Id}-location`,
            date: raw[i].Date,
            StatusId: raw[i].StatusId,
            text: `${this.$t('modalni.asset.promjenaLokacije')} ${v(raw[i - 1].LocationName)} → ${v(raw[i].LocationName)}`
          })
        }

        // PROMJENA SOBE
        if (raw[i - 1].RoomId !== raw[i].RoomId) {
          history.push({
            id: `${raw[i].Id}-room`,
            date: raw[i].Date,
            StatusId: raw[i].StatusId,
            text: `${this.$t('modalni.asset.premjestaj')} ${v(raw[i - 1].RoomName)} → ${v(raw[i].RoomName)}`
          })
        }

        // PROMJENA STATUSA (samo ako oba statusa postoje)
        if (
          raw[i - 1].StatusId !== raw[i].StatusId &&
          raw[i - 1].StatusId != null &&
          raw[i].StatusId != null
        ) {
          const statusMap = {
            1: this.$t('general.aktivan'),
            2: this.$t('general.neaktivan'),
            3: this.$t('general.rashodovan'),
            4: this.$t('general.predlogZaRashod')
          }

          history.push({
            id: `${raw[i].Id}-status`,
            date: raw[i].Date,
            StatusId: raw[i].StatusId,
            text: `${this.$t('modalni.asset.promjenaStatusa1')} ${statusMap[raw[i - 1].StatusId]} → ${statusMap[raw[i].StatusId]}`
          })
        }

        // PROMJENA NOTE-a
        if (raw[i - 1].Note !== raw[i].Note) {
          history.push({
            id: `${raw[i].Id}-note`,
            date: raw[i].Date,
            StatusId: raw[i].StatusId,
            text: `${this.$t('modalni.asset.napomena')}: ${raw[i].Note ?? '—'}`
          })
        }

        // PROMJENA REVERS BROJA
        if (raw[i - 1].ReverseNumber !== raw[i].ReverseNumber) {
          history.push({
            id: `${raw[i].Id}-reverse`,
            date: raw[i].Date,
            StatusId: raw[i].StatusId,
            text: `${this.$t('modalni.asset.promjenaReversBroja')} ${v(raw[i - 1].ReverseNumber)} → ${v(raw[i].ReverseNumber)}`
          })
        }
      }

      return history
    },
    //otvaranje prozora za promjenu statusa stalnog sredstva
    askConfirmStatusChange(item, newStatusId) {
      if (item.StatusId === newStatusId) return

      this.pendingStatusChange = {
        asset: item,
        oldStatusId: item.StatusId,
        newStatusId
      }

      this.confirmStatusDialog = true
    },
    //promjena statusa stalnog sredsta nakon potvrde
    async confirmStatusChange() {
      const { asset, newStatusId, oldStatusId } = this.pendingStatusChange
      if (!asset) return
      asset.StatusId = newStatusId

      try {
        await this.updateAssetStatus(asset.Id, newStatusId, asset.UserId, this.statusChangeNote)

        asset.StatusName =
          this.statusOptions.find(s => s.id === newStatusId)?.name || ''

        this.confirmStatusDialog = false
        this.statusChangeNote = ''
        this.pendingStatusChange = {
          asset: null,
          newStatusId: null,
          oldStatusId: null
        }
      } catch {
        asset.StatusId = oldStatusId
        asset.StatusName =
          this.statusOptions.find(s => s.id === oldStatusId)?.name || ''
        this.confirmStatusDialog = false
        this.statusChangeNote = ''
        this.pendingStatusChange = {
          asset: null,
          newStatusId: null,
          oldStatusId: null
        }
      }
    },
    cancelStatusChange() {
      this.confirmStatusDialog = false
      this.statusChangeNote = ''
      this.pendingStatusChange = {
        asset: null,
        newStatusId: null,
        oldStatusId: null
      }
    },
    rowClass(item) {
      if (item.StatusId === 3) return 'row-rashodovano'
      if (item.StatusId === 4) return 'row-predlog'
      if (item.StatusId === 2) return 'row-neaktivno'
      if (this.isOlderThan7Years(item.Date)) return 'row-old'
      if (!item.UserId) return 'row-unassigned'
      return ''
    },
    //update statusa stalnog sredstva
    async updateAssetStatus(assetId, statusId, userId, note = '') {
      try {
        await this.$axios.put(
          'Asset/StalnaSredstva/EditStatus',
          {
            AssetId: Number(assetId),
            CompanyId: Number(localStorage.getItem('CompanyId')),
            StatusId: Number(statusId),
            UserId: userId ? Number(userId) : null,
            Note: note
          }
        )

        this.$store.dispatch("NOTIF_SUCCESS", {
          poruka: this.$t('User_message.m51'),
          position: "top-end",
          timer: 2500,
          action: "noreload"
        })

        await this.loadAssets()
      } catch (err) {
        this.$store.dispatch("NOTIF_WARNING", {
          poruka: this.$t('User_message.m52'),
          position: "center",
          icon: "error",
          timer: 4500,
          width: "550"
        })
      }
    },
    async viewAsset(asset) {
      this.viewMode = true
      this.editMode = false
      this.selectedId = asset.Id

      this.form = {
        InvNumber: asset.InvNumber,
        ReverseNumber: asset.ReverseNumber,
        AssetName: asset.Name,
        category: asset.CategoryId,
        assigned_to: asset.UserId ? Number(asset.UserId) : null,
        room: null,
        location: asset.LocationId,
        PurchasePrice: asset.Price ?? null,
        Date: asset.Date,
        AssetDescription: asset.Description,
        Note: asset.Note,
        Supplier: asset.Supplier,
        PrNumber: asset.PrNumber,
        Quantity: Number(asset.Quantity ?? 1),
        SerialNumber: asset.SerialNumber,
        StatusId: asset.StatusId,
      }

      await this.onLocationChange(asset.LocationId)
      this.form.room = asset.RoomId

      this.dialog = true
    },
    printBarcode(asset) {
      printBarCodeF({
        title: 'Barkod',
        invNumber: asset.InvNumber,
        invText: this.$t('table.asset.InvBr'),
        assetName: asset.Name
      })
    },
    async onLocationChange(locationId) {
      this.form.room = null
      this.rooms = []

      if (!locationId) return

      try {
        const r = await this.$axios.post(
          'Location/Rooms',
          {
            LocationId: Number(locationId),
            CompanyId: Number(localStorage.getItem('CompanyId')),
            LanguageId: Number(localStorage.getItem('LanguageId'))
          }
        )

        this.rooms = r.data

      } catch (err) {
        console.error(this.$t('general.noData'), err)
        this.rooms = []
      }
    },
    openDeleteDialog(asset) {
      this.deleteAssetId = asset.Id
      this.deleteAssetName = asset.Name
      this.deleteDialog = true
    },
    async confirmDelete() {
      if (!this.deleteAssetId) return

      try {
        const r = await this.$axios.put(
          'Asset/StalnaSredstva/Delete/' + this.deleteAssetId
        )

        if (r.status === 200) {
          this.$store.dispatch("NOTIF_SUCCESS", {
            poruka: this.$t('User_message.m43'),
            position: "top-end",
            timer: 2500,
            action: "noreload"

          })

          await this.loadAssets()
        }
      } catch (err) {
        this.$store.dispatch("NOTIF_WARNING", {
          poruka: this.$t('User_message.m44'),
          position: "center",
          icon: "error",
          timer: 4500,
          width: "550"
        })
      } finally {
        this.deleteDialog = false
        this.deleteAssetId = null
        this.deleteAssetName = ''
      }
    },

    async fetchUsers() {

      try {

        var data = {
          Id: Number(localStorage.getItem('CompanyId')),
          LanguageId: Number(localStorage.getItem('LanguageId'))
        }

        const r = await this.$axios.post("User/Show", data)

        this.users = r.data

        // dodavanje Neraspoređeno
        this.users = [
          {
            UserId: 0,
            Name: this.$t('table.asset.nerasporedjeno')
          },
          ...this.users
        ]

      } catch (err) {
        console.error(this.$t('general.noData'), err)
        this.users = []
      }
    },
    async fetchLocations() {

      try {

        var data = {
          Id: Number(localStorage.getItem('CompanyId')),
          LanguageId: Number(localStorage.getItem('LanguageId'))
        }

        const r = await this.$axios.post("Location/Locations", data)

        this.locations = r.data

      } catch (err) {
        console.error(this.$t('general.noData'), err)
        this.locations = []
      }
    },

    async fetchAssetCategories() {

      try {

        var data = {
          Id: Number(localStorage.getItem('CompanyId')),
          LanguageId: Number(localStorage.getItem('LanguageId'))
        }

        const r = await this.$axios.post("Category/Show", data)

        this.assetCategory = r.data

      } catch (err) {
        console.error(this.$t('general.noData'), err)
        this.assetCategory = []
      }
    },

    async fetchAssets() {

      this.loading = true

      try {

        var data = {
          Id: Number(localStorage.getItem('CompanyId')),
          LanguageId: Number(localStorage.getItem('LanguageId'))
        }

        const r = await this.$axios.post('Asset/StalnaSredstva/', data)

        this.assets = r.data

      } catch (err) {
        console.error(this.$t('general.noData'), err)
        this.assets = []
      } finally {
        this.loading = false
      }

    },
    isOlderThan7Years(dateStr) {
      if (!dateStr) return false;
      const purchase = new Date(dateStr);
      const now = new Date();
      return (now - purchase) / (1000 * 60 * 60 * 24 * 365.25) > 7;
    },
    addAsset() {
      this.viewMode = false
      this.editMode = false
      this.selectedId = null
      this.resetForm()
      this.form.StatusId = 1
      this.dialog = true

      this.$nextTick(() => {
        this.$refs.assetForm?.resetValidation()
      })
    },

    async editAsset(asset) {
      this.viewMode = false
      this.editMode = true
      this.selectedId = asset.Id

      this.form = {
        InvNumber: asset.InvNumber,
        ReverseNumber: asset.ReverseNumber,
        AssetName: asset.Name,
        category: asset.CategoryId,
        assigned_to: asset.UserId ? Number(asset.UserId) : null,
        room: null,
        location: asset.LocationId,
        PurchasePrice: asset.Price ?? null,
        Date: asset.Date,
        AssetDescription: asset.Description,
        Note: asset.Note,
        Supplier: asset.Supplier,
        PrNumber: asset.PrNumber,
        Quantity: Number(asset.Quantity ?? 1),
        SerialNumber: asset.SerialNumber,
        StatusId: asset.StatusId,
      }
      await this.onLocationChange(asset.LocationId)
      this.form.room = asset.RoomId

      this.dialog = true;

      this.$nextTick(() => {
        this.$refs.assetForm?.resetValidation()
      })
    },
    async saveAsset() {
      if (this.saving) return
      this.saving = true

      try {
        if (this.editMode && this.form.StatusId === 3) {
          this.$store.dispatch("NOTIF_WARNING", {
            poruka: this.$t('User_message.m55'),
            position: "center",
            icon: "error",
            timer: 4500,
            width: "550"
          })
          return
        }

        const { valid } = await this.$refs.assetForm.validate()
        if (!valid) return

        const payload = {
          Id: this.selectedId,
          CompanyId: Number(localStorage.getItem("CompanyId")),
          CategoryId: this.form.category,
          InvNumber: this.form.InvNumber,
          ReverseNumber: this.form.ReverseNumber,
          Name: this.form.AssetName,
          UserId: this.form.assigned_to === 0 ? null : this.form.assigned_to,
          LocationId: this.form.location,
          RoomId: this.form.room,
          Price: this.parseMoney(this.form.PurchasePrice),
          Date: this.form.Date ? `${this.form.Date}T00:00:00Z` : null,
          Description: this.form.AssetDescription,
          Supplier: this.form.Supplier,
          PrNumber: this.form.PrNumber,
          Quantity: Number(this.form.Quantity),
          SerialNumber: this.form.SerialNumber,
          Note: this.form.Note,
          StatusId: this.form.StatusId,
        }

        if (this.editMode) {
          await this.$axios.put('Asset/StalnaSredstva/Edit', payload)
          this.$store.dispatch("NOTIF_SUCCESS", {
            poruka: this.$t('User_message.m41'),
            position: "top-end",
            timer: 2500,
            action: "noreload"
          })
        } else {
          const addPayload = { ...payload }
          delete addPayload.Id
          await this.$axios.post('Asset/StalnaSredstva/Add', addPayload)
          this.$store.dispatch("NOTIF_SUCCESS", {
            poruka: this.$t('User_message.m39'),
            position: "top-end",
            timer: 2500,
            action: "noreload"
          })
        }

        this.dialog = false

        if (this.popisneAkcijeEditItem) {
          await this.prihvatiAkciju(this.popisneAkcijeEditItem)
          this.popisneAkcijeEditItem = null
          this.popisneAkcijeDialog = true
        }

        await this.loadAssets()

      } catch (err) {
        this.$store.dispatch("NOTIF_WARNING", {
          poruka: this.$t('User_message.m42'),
          position: "center",
          icon: "error",
          timer: 4500,
          width: "550"
        })
      } finally {
        this.saving = false
      }
    },

    closeDialog() {
      this.dialog = false
      this.viewMode = false
      this.editMode = false
      this.selectedId = null
      this.popisneAkcijeEditItem = null
      this.resetForm();

      this.$nextTick(() => {
        this.$refs.assetForm?.resetValidation()
      })
    },

    resetForm() {
      this.form = {
        InvNumber: 0,
        ReverseNumber: "",
        AssetName: "",
        category: null,
        assigned_to: null,
        room: null,
        location: null,
        PurchasePrice: null,
        Date: null,
        AssetDescription: "",
        Note: "",
        Supplier: "",
        PrNumber: "",
        Quantity: 1,
        SerialNumber: "",
        StatusId: null,
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return "";
      try {
        return format(new Date(dateStr), "dd.MM.yyyy.");
      } catch {
        return dateStr;
      }
    },

    async exportToExcel() {
      const assets = this.filteredAssets || []

      if (!assets.length) {
        this.$store.dispatch("NOTIF_WARNING", {
          poruka: this.$t('exportToExcel.noData'),
          position: "center",
          icon: "error",
          timer: 4500,
          width: "550"
        })
        return
      }

      const workbook = new ExcelJS.Workbook()
      const worksheet = workbook.addWorksheet(this.$t('exportToExcel.stalnaSredstva'))


      /* ================= NASLOV ================= */
      worksheet.mergeCells("A3:O3")
      worksheet.getCell("A3").value = this.$t('exportToExcel.pregledStalnihSredstava')
      worksheet.getCell("A3").font = { size: 14, bold: true }
      worksheet.getCell("A3").alignment = { horizontal: "center" }

      /* ================= HEADER (RED 6) ================= */
      worksheet.getRow(6).values = [
        this.$t('exportToExcel.invBroj'),
        this.$t('exportToExcel.brojReversa'),
        this.$t('exportToExcel.sredstvo'),
        this.$t('exportToExcel.zaduzenaOsoba'),
        this.$t('exportToExcel.soba'),
        this.$t('exportToExcel.lokacija'),
        this.$t('exportToExcel.nabavnaVr'),
        this.$t('exportToExcel.datumNabavke'),
        this.$t('exportToExcel.dobavljac'),
        this.$t('exportToExcel.brojPR'),
        this.$t('exportToExcel.kom'),
        this.$t('exportToExcel.serijskiBroj'),
        this.$t('exportToExcel.status'),
      ]

      worksheet.getRow(6).font = { bold: true }

      /* ================= ŠIRINE KOLONA ================= */
      worksheet.columns = [
        { width: 8 }, // Inv. broj
        { width: 15 }, // Broj reversa
        { width: 40 }, // Sredstvo
        { width: 20 }, // Zadužena osoba
        { width: 10 }, // Soba
        { width: 20 }, // Lokacija
        { width: 15 }, // Nabavna vr.
        { width: 15 }, // Datum
        { width: 20 }, // Dobavljač
        { width: 15 }, // Broj PR
        { width: 5 },  // Kom.
        { width: 18 }, // S/N
        { width: 10 }, // Status
      ]
      worksheet.getColumn(7).numFmt = '#,##0.00'
      // Datum nabavke (kolona 8 = H)
      worksheet.getColumn(8).alignment = { horizontal: "center" }

      /* ================= PODACI (OD REDA 7) ================= */
      let rowIndex = 7

      assets.forEach(a => {
        worksheet.getRow(rowIndex).values = [
          a.InvNumber ?? "",
          a.ReverseNumber ?? "",
          a.Name ?? "",
          a.UName ?? this.$t('asset.nerasporedjeno'),
          a.RoomName ?? "",
          a.LocationName ?? "",
          Number(a.Price ?? 0),
          this.formatDate(a.Date),
          a.Supplier ?? "",
          a.PrNumber ?? "",
          a.Quantity ?? 1,
          a.SerialNumber ?? "",
          a.StatusName ?? "",
        ]
        rowIndex++
      })

      /* ================= ZAMRZNI HEADER ================= */

      worksheet.views = [{ state: "frozen", ySplit: 6 }]

      /* ================= FOOTER ================= */
      worksheet.addRow([])
      worksheet.addRow([
        `${this.$t('exportToExcel.pokrenuo')} ${localStorage.getItem("Name")}`
      ])
      worksheet.addRow([
        `${this.$t('exportToExcel.datumKreiranja')} ${format(new Date(), "dd.MM.yyyy.")}`
      ])

      /* ================= SAVE ================= */
      const buffer = await workbook.xlsx.writeBuffer()

      saveAs(
        new Blob(
          [buffer],
          { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }
        ),
        `${this.$t('exportToExcel.stalnaSredstva')}.xlsx`
      )
    },

    async openHistory(assetId) {
      this.historyDialog = true
      this.activationDateValue = null
      this.assetHistory = []

      try {
        const res = await this.$axios.post(
          'Asset/StalnaSredstva_Istorijat',
          {
            CompanyId: Number(localStorage.getItem('CompanyId')),
            AssetId: assetId,
            LanguageId: Number(localStorage.getItem('LanguageId'))
          }
        )

        const raw = Array.isArray(res.data) ? res.data : []

        this.assetHistory = this.mapHistory(raw)

        // DATUM AKTIVACIJE
        const activated = raw.find((h, index, arr) => {
          if (h.StatusId !== 1) return false

          const prev = arr[index - 1]
          return !prev || prev.StatusId !== 1
        })

        this.activationDateValue =
          activated?.Date ??
          this.assets.find(a => a.Id === assetId)?.Date ??
          null

      } catch (err) {
        console.error(this.$t('User_message.m113'), err)

        this.rawAssetHistory = []
        this.assetHistory = []
        this.activationDateValue = null
      }
    }
  },
};
</script>

<style scoped>
@keyframes blink {

  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.3;
  }
}

.blink-btn {
  animation: blink 1.2s ease-in-out infinite;
}
</style>